/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, Observable, Subject, catchError, switchMap, take, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ToasterService } from '../../services/toaster.service';
import { AuthService } from '../../services/auth.service';
import { responseStatus } from '../constants/responseStatus.const';

@Injectable({
  providedIn: 'root'
})
export class GetInterceptor implements HttpInterceptor {
  /** True while a single access-token refresh call is in flight — guards against firing N refresh calls for N concurrent 401s. */
  private isRefreshing = false;
  /** One-shot broadcast per refresh cycle: true = refreshed ok (waiters retry), false = refresh failed (waiters fall through to logout). */
  private refreshResult$ = new Subject<boolean>();

  constructor(
    private _toastrService: ToasterService,
    private router: Router,
    private translateService: TranslateService,
    private authService: AuthService
  ) {}

  /**
   * Avoid showing raw Joi / backend validation strings (e.g. "must be a safe number").
   */
  private formatBadRequestMessage(error: HttpErrorResponse): string {
    const raw = error.error?.message;
    const text = Array.isArray(raw) ? raw.join(' ') : String(raw ?? '');
    if (/max_word_count/i.test(text) && /safe number/i.test(text)) {
      if (error.url?.includes('essayTemplate')) {
        return this.translateService.instant('EssayTemplateMaxWordCountRange');
      }
      return this.translateService.instant('EssayTopicMaxWordCountRange');
    }
    if (Array.isArray(raw)) {
      return this.translateService.instant('ValidationErrorGeneric');
    }
    if (typeof raw === 'string' && raw.trim().length > 0) {
      return raw;
    }
    return this.translateService.instant('ValidationErrorGeneric');
  }

  /** 401 with a still-valid session (i.e. access denied, not a token expiry) → hard logout, same as before. */
  private isUnauthorizedNotExpired(error: HttpErrorResponse): boolean {
    const isSessionExpired = (error?.error as { isSessionExpired?: unknown })?.isSessionExpired === true;
    return error['status'] === responseStatus.UNAUTHORIZED && isSessionExpired === false;
  }

  /** 401 that represents a genuinely expired access token — the case we can recover from via refresh. */
  private isTokenExpired401(error: HttpErrorResponse): boolean {
    return error['status'] === responseStatus.UNAUTHORIZED && !this.isUnauthorizedNotExpired(error);
  }

  /** Pre-auth / refresh endpoints — never attempt a token refresh for these (no session yet, or would recurse). */
  private isAuthBypassUrl(url: string): boolean {
    const lower = url.toLowerCase();
    return lower.includes('user/login')
      || lower.includes('user/refresh-token')
      || lower.includes('user/verifyotp')
      || lower.includes('user/forgotpassword')
      || lower.includes('user/sendotp');
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return this.handle(req, next, false);
  }

  private handle(
    req: HttpRequest<any>,
    next: HttpHandler,
    isRetry: boolean
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (!(error instanceof HttpErrorResponse)) {
          return throwError(() => error);
        }

        // Genuinely expired access token (not already a retry, not a pre-auth/refresh endpoint) — refresh and retry.
        if (
          !isRetry &&
          error['status'] === responseStatus.UNAUTHORIZED &&
          this.isTokenExpired401(error) &&
          !this.isAuthBypassUrl(req.url)
        ) {
          // No refresh token left (e.g. a request still in flight after the user logged out) —
          // nothing to recover, so just make sure we're on the login page, without a toast.
          if (!this.authService.getRefreshToken()) {
            this.handleLogout();
            return EMPTY;
          }
          return this.handleTokenExpiry(req, next);
        }

        // Refresh endpoint itself rejected, or a retry still came back as an expired session —
        // the session is over; log out silently instead of surfacing the raw backend message.
        if (
          error['status'] === responseStatus.UNAUTHORIZED &&
          this.isTokenExpired401(error) &&
          (isRetry || req.url.toLowerCase().includes('user/refresh-token'))
        ) {
          this.handleLogout();
          return throwError(() => error);
        }

        this.reportGenericError(error, req);
        return throwError(() => error);
      })
    );
  }

  /**
   * Ensures only one refresh-token call is in flight at a time. Every 401 that lands here — whether it's the
   * one that kicks off the refresh, or one that arrives while a refresh is already running — subscribes to the
   * same `refreshResult$` broadcast and retries (or falls through to logout) with its own original `req`.
   */
  private handleTokenExpiry(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      // getOrRefreshToken() shares one in-flight request across this interceptor, the
      // proactive timer, and app-bootstrap, and stores the resulting tokens itself.
      this.authService.getOrRefreshToken().subscribe({
        next: () => {
          this.isRefreshing = false;
          this.refreshResult$.next(true);
        },
        error: () => {
          this.isRefreshing = false;
          this.refreshResult$.next(false);
        }
      });
    }

    return this.refreshResult$.pipe(
      take(1),
      switchMap((refreshed) => {
        if (refreshed) {
          return this.handle(req, next, true);
        }
        this.handleLogout();
        return EMPTY;
      })
    );
  }

  /** Silent logout for an expired session — the redirect to login is enough, a toast here is just noise. */
  private handleLogout(): void {
    this.authService.stopRefreshTimer();
    this.authService.profileDetails.next(null);
    localStorage.clear();
    this.router.navigate(['/auth/login']);
  }

  private reportGenericError(error: HttpErrorResponse, req: HttpRequest<any>): void {
    switch (error['status']) {
    case responseStatus.BADREQUEST: {
      const excludedUrl = 'admin/categories/sub';
      // Background premium check (sidebar + route guard) — a School Manager with no linked school shouldn't get a toast on every page load.
      const isBenchmarksAccessCheck = /\/national-benchmarks\/access$/.test(req.url ?? '');

      if (!req.url?.includes(excludedUrl) && !isBenchmarksAccessCheck) {
        this._toastrService.errToastr(
          this.formatBadRequestMessage(error)
        );
      }
      break;
    }
    case responseStatus.UNAUTHORIZED:
    case responseStatus.FORBIDDEN: {
      // A premium-locked school gets 403 from National Benchmarks — that's a plan restriction, not a dead session.
      if (error['status'] === responseStatus.FORBIDDEN && req.url?.includes('national-benchmarks')) {
        break;
      }
      // Only account-state rejections (blocked / suspended / login disabled) are worth telling
      // the user about; a plain expired/invalid session logs out silently.
      if (
        error['status'] === responseStatus.FORBIDDEN ||
        this.isUnauthorizedNotExpired(error) ||
        (this.isAuthBypassUrl(req.url) && !req.url.toLowerCase().includes('user/refresh-token'))
      ) {
        this._toastrService.errToastr(error['error']['message']);
      }
      this.authService.stopRefreshTimer();
      localStorage.clear();
      this.router.navigate(['/auth/login']);
      break;
    }
    case responseStatus.REQUESTTIMEOUT: {
      this._toastrService.errToastr(error['error']['message']);
      break;
    }
    case responseStatus.NOTFOUND:
    case responseStatus.BadGateway: {
      // Consent-status lookup is polled speculatively (e.g. View Student) — no consent
      // request existing yet is an expected state, not an error worth surfacing.
      const isConsentStatusLookup = req.method === 'GET' && /\/students\/[^/]+\/consent$/.test(req.url ?? '');
      if (isConsentStatusLookup) {
        break;
      }
      const message = error.error?.message;
      const hasMessage =
        typeof message === 'string' && message.trim().length > 0;
      this._toastrService.errToastr(
        hasMessage ? message : this.translateService.instant('networkConnection')
      );
      break;
    }
    case responseStatus.INTERNALSERVERERROR:
      this._toastrService.errToastr(error['error']['message']);
      break;
    case responseStatus.RequestTookMoreThanExpected:
      this._toastrService.errToastr(error['error']['message']);
      break;
    case responseStatus.GatewayTimeOut:
      this._toastrService.errToastr(error['error']['message']);
      break;
    default: {
      // RAG knowledge base errors (409 duplicate name / folder not empty,
      // 413 file too large) carry a safe, specific message — show it rather
      // than a misleading connection error. A non-empty folder delete is
      // handled by the page itself with an "open folder" dialog.
      const isRagRequest = /\/admin\/rag\//.test(req.url ?? '');
      const isRagFolderDeleteConflict =
        isRagRequest &&
        req.method === 'DELETE' &&
        error['status'] === 409 &&
        /\/documents\/folders\/[^/]+$/.test(req.url ?? '');
      if (isRagFolderDeleteConflict) {
        break;
      }
      const message = error.error?.message;
      this._toastrService.errToastr(
        isRagRequest && typeof message === 'string' && message.trim().length > 0
          ? message
          : this.translateService.instant('networkConnection')
      );
    }
    }
  }
}
